/**
 * The portrait parts, authored in code.
 *
 * The art pipeline's verdict (`reports/captain/VERDICT.md`) was that a diffusion model
 * cannot hold the anchor contract at 64 px: canny conditioning held the eye line and
 * nothing else, mirror residual ran 23–49 % on every variant, and the eight generated
 * eyes were not nameable apart. The control arm — the same eight authored in code, six
 * parameters each — cleared the contract 8/8 with a mirror residual of exactly zero.
 * So the parts live here, in the house style of `src/render/silhouettes.ts` and
 * `src/render/ship.ts`: geometry from parameters, palette compliance by construction.
 *
 * Everything writes ramp INDICES into a flat `Uint8Array(64*64)`. Index 0 is
 * transparent; 1…10 resolve through `RAMP_TOKENS`. No colour is named in this file and
 * none can be, which is the whole point of the indirection.
 *
 * Four things the second pass fixed, because the first pass held every anchor and still
 * drew nobody:
 *
 *  - **The head varies above the eye line.** Nine face shapes moved the jaw and left
 *    rows 8–41 pixel-identical, so 2 048 captains shared 186 upper silhouettes. Cranium
 *    and temple now move too, which is where an eye reads a head.
 *  - **Hair leaves the skull.** It was clamped to 2 px of growth in every direction, so
 *    the one slot that could carry a silhouette declined to. Afros, tails and long side
 *    masses now break the outline by 6–8 px.
 *  - **Nothing is stamped at a literal address.** Every highlight and every mark is
 *    anchored to `halfWidth`, to an anchor row, or to a per-captain offset.
 *  - **A face reads against the panel it ships on.** `pal.ui.bg` sits between the ink
 *    and the specular, and the first ramp guarded only the two ends: a quarter of the
 *    roster dissolved into the background. Every mass now carries a contour value chosen
 *    against the field, not against itself.
 *
 * Symmetry is authored, not hoped for: mirrored features are written for x ≤ 31 through
 * `putSym`. Four operations deliberately break it — gaze, brow lift, mouth pull, lid
 * aperture — and all four stay inside slots the contract already declares unmirrored, so
 * the mirror gate keeps its teeth everywhere else.
 *
 * Nothing here allocates beyond a fixed module-level profile scratch. A portrait is
 * painted once per captain.
 */

import type { Appearance } from './seed';
import { CANVAS, ROW, PUPIL_X, SLOTS, RC, V, blooms, needsRim, isSkin } from './anchors';
import { LAYERS, hasLayer } from './slots';

const W = CANVAS;

// ---------------------------------------------------------------------------
// Ramp assignments — genes to ramp indices
// ---------------------------------------------------------------------------

export interface FaceRamp {
  skinShadow: number;
  skinBase: number;
  skinLight: number;
  /** Contour for the head. Lightened where the skin would lose its edge to the panel. */
  skinEdge: number;
  hairDark: number;
  hairLight: number;
  hairEdge: number;
  iris: number;
  clothDark: number;
  clothMid: number;
  clothLight: number;
  clothEdge: number;
  /** Which single feature spends `pal.ui.accent`. Exactly one per captain. */
  warm: 'iris' | 'cap' | 'collar' | 'rank';
}

/**
 * The contour a mass of value `fill` needs in order to be a shape rather than a stain.
 *
 * `V.ink` is `#000000` and the panel behind a portrait is `pal.ui.bg` `#050A14`. Those
 * two are the same colour to an eye, so an inked edge only works when what it encloses
 * is brighter than the panel. Below that, the edge has to be the bright thing: the mass
 * gets a rim instead of an outline, which is also how a dark subject reads on a CRT.
 *
 * The rim is drawn out of the mass's own arc. A cyan rim around a brown cheek reads as
 * a light source somewhere off to the side of the panel rather than as the edge of a
 * head, which is exactly the artefact the AMD-42 complexion ramp was added to remove.
 */
function contour(fill: number): number {
  if (!needsRim(fill)) return V.ink;
  return isSkin(fill) ? V.skin4 : V.midlight;
}

/**
 * Fourteen complexions, dark to light, three values each: shadow, base, highlight.
 *
 * Every entry is a member of the AMD-42 `pal.skin.*` arc. The table this replaced was
 * fourteen triples of §11.2.6 sonar blue that varied in lightness and never in hue — the
 * measurement that killed it is in `reports/captain/SKIN-RAMP.md`, and the short version
 * is that a portrait was 88.9 % mid-blue and complexion carried zero bits of identity.
 *
 * Two rules the table obeys, both enforced by `tests/captain-portrait.test.ts`:
 *
 *  - **`V.skin0` is never a base.** It measures 0.077 of displayed luminance against
 *    `pal.ui.bg` and `RC.minEdgeContrast` is 0.10, so a head filled with it dissolves
 *    into the panel. It appears only as a shadow, which is the rule the old table obeyed
 *    with `V.deep`.
 *  - **The base is the complexion.** Six of the eight values are reachable as a base and
 *    the fourteen genes spread across all six, so the roster carries six complexions
 *    rather than the four the six-value first draft of this ramp would have allowed.
 *    Within a complexion the shadow depth and the highlight reach still move, which is
 *    what makes two captains of the same colour different people under the same light.
 */
const SKIN: readonly (readonly [number, number, number])[] = [
  [V.skin0, V.skin1, V.skin2],
  [V.skin0, V.skin1, V.skin3],
  [V.skin1, V.skin2, V.skin3],
  [V.skin0, V.skin2, V.skin4],
  [V.skin1, V.skin3, V.skin4],
  [V.skin2, V.skin3, V.skin5],
  [V.skin1, V.skin4, V.skin5],
  [V.skin2, V.skin4, V.skin6],
  [V.skin3, V.skin4, V.skin5],
  [V.skin2, V.skin5, V.skin6],
  [V.skin3, V.skin5, V.skin7],
  [V.skin4, V.skin5, V.skin6],
  [V.skin3, V.skin6, V.skin7],
  [V.skin4, V.skin6, V.skin7],
];

/**
 * Nine hair tints. The last two are the grey end `seed.ts` reaches for with age.
 *
 * Black stays cool, because black hair on any complexion is a near-neutral dark and the
 * §11.2.6 shadows are already that. The natural browns and the blonde end come out of the
 * complexion arc — a head of hair drawn in sonar blue on a brown face is a wig. Grey and
 * white stay cool for the same reason black does, and because `pal.ui.textdim` and
 * `pal.ui.text` are the two values in the whole ramp that read as unpigmented.
 */
const HAIR: readonly (readonly [number, number])[] = [
  [V.shade, V.cool], // black
  [V.skin0, V.skin2], // darkest brown
  [V.skin1, V.skin3],
  [V.skin2, V.skin4],
  [V.skin3, V.skin5], // auburn
  [V.skin4, V.skin6],
  [V.skin5, V.skin7], // blonde
  [V.midlight, V.light], // greying
  [V.light, V.high], // white
];

/**
 * Seven iris values. Five read as eye colour against the `#E8F4FF` sclera; the sixth is
 * the warm accent and the seventh is blown out — a clouded, sightless eye, and the one a
 * player describes out loud. `seed.ts` weights both of those down to rarities.
 *
 * The dark end used to reach `V.ink` and `V.deep`. At five pixels of iris in an eleven
 * pixel eye that is not an eye colour, it is a hole: dark captains came back from the
 * tube with two empty sockets.
 *
 * Two of the five common tints are brown, because when the whole ramp was §11.2.6 every
 * legal iris was a blue and the roster came out of the tube with 100 % ice eyes. Two
 * browns and two blues against one pale grey is a crew, not a costume.
 *
 * Index 5 is load-bearing: `warmCarrier` reads it directly, so a value may move but the
 * position of `V.warm` may not.
 */
const IRIS: readonly number[] = [V.skin1, V.skin3, V.cool, V.midlight, V.light, V.warm, V.high];

/** Seven uniform cloths, floored the same way the skin is. */
const CLOTH: readonly (readonly [number, number, number])[] = [
  [V.shade, V.cool, V.mid],
  [V.shade, V.cool, V.midlight],
  [V.cool, V.mid, V.midlight],
  [V.cool, V.midlight, V.light],
  [V.mid, V.midlight, V.light],
  [V.mid, V.midlight, V.high],
  [V.midlight, V.light, V.high],
];

/**
 * Which feature spends the one warm value.
 *
 * `pal.ui.accent` is the only non-blue entry in an eleven-value ramp, so it is the
 * strongest attention magnet on the canvas and it can only be spent once. A gold iris
 * outranks a cap band, a cap band outranks a collar cord, and the rank flash takes what
 * is left — which is most captains, and is why the flash moved onto the shoulder where
 * it frames the jaw instead of dragging the eye into a corner.
 */
function warmCarrier(a: Appearance): FaceRamp['warm'] {
  if (a.eyeTint % IRIS.length === 5) return 'iris';
  // The peaked officer cap, and only when the captain is actually wearing one. Before
  // headwear became an optional layer, index 0 meant "none" and this test read it as a
  // hat: a bare-headed captain could be assigned a cap band that nothing ever painted,
  // and the warm value went unspent.
  if (a.headwear % HEADWEAR_COUNT === 3 && hasLayer(a.presence, 'headwear')) return 'cap';
  if (a.uniform % 8 === 4) return 'collar';
  return 'rank';
}

/** Ramp steps a hair mass has to hold away from the complexion growing it. */
const HAIR_SKIN_GAP = 2;

/**
 * Pushes a hair pair off the face it sits on.
 *
 * Hair and complexion now draw from one arc, and `hairTint` is rolled independently of
 * `skinTone`, so a third of the roster would otherwise be issued hair within a step of
 * its own forehead. An inked outline still separates the mass, but a differently shaped
 * forehead is not a hairstyle — the whole reason hair owns a slot wider and deeper than
 * the skull is that it is the one part that can change a silhouette.
 *
 * Cool hair — black, greying, white — is left alone: it is already a different family
 * from every complexion and cannot collide with one.
 *
 * The push is toward whichever end of the arc has room, which is also the direction that
 * looks right: a pale captain gets darker hair, a dark captain lighter. The span between
 * dark and light is preserved, so the tint keeps its own contrast.
 */
function partHair(dark: number, light: number, skinBase: number): readonly [number, number] {
  if (!isSkin(dark) || Math.abs(dark - skinBase) >= HAIR_SKIN_GAP) return [dark, light];
  const span = light - dark;
  const shifted = skinBase >= V.skin4 ? skinBase - HAIR_SKIN_GAP : skinBase + HAIR_SKIN_GAP;
  const d = Math.min(Math.max(shifted, V.skin0), V.skin7 - span);
  return [d, d + span];
}

export function faceRamp(a: Appearance): FaceRamp {
  const s = SKIN[a.skinTone % SKIN.length];
  const h0 = HAIR[a.hairTint % HAIR.length];
  const h = partHair(h0[0], h0[1], s[1]);
  const c = CLOTH[a.uniformTint % CLOTH.length];
  const warm = warmCarrier(a);
  return {
    skinShadow: s[0],
    skinBase: s[1],
    skinLight: s[2],
    skinEdge: contour(s[1]),
    hairDark: h[0],
    hairLight: h[1],
    hairEdge: contour(h[0]),
    iris: warm === 'iris' ? V.warm : IRIS[a.eyeTint % IRIS.length],
    clothDark: c[0],
    clothMid: c[1],
    clothLight: c[2],
    clothEdge: contour(c[0]),
    warm,
  };
}

// ---------------------------------------------------------------------------
// The four unmirrored operations
// ---------------------------------------------------------------------------

export interface Asymmetry {
  /** Iris offset in px, the same absolute direction in both eyes. This is a gaze. */
  gaze: number;
  /** 0 = the captain's left brow, 1 = the right. */
  browSide: number;
  browLift: number;
  mouthSide: number;
  /** −1 pulled down, +1 pulled up, on one corner only. */
  mouthPull: number;
  /** Which eye sits a pixel wider open. */
  wideEye: number;
}

/**
 * `asymmetry` is one integer, not six genes, so adding it cost the seed one draw.
 *
 * If every magnitude lands on zero the brow is forced up a pixel. A captain with no
 * asymmetry at all is the identikit this gene exists to end, and 1 in 27 of them would
 * be exactly that.
 */
export function decodeAsymmetry(n: number): Asymmetry {
  const v = ((n % 216) + 216) % 216;
  const gaze = [-1, 0, 1][v % 3];
  const browLift = [0, 1, 2][Math.floor(v / 3) % 3];
  let mouthPull = [-1, 0, 1][Math.floor(v / 9) % 3];
  const browSide = Math.floor(v / 27) % 2;
  const mouthSide = Math.floor(v / 54) % 2;
  const wideEye = Math.floor(v / 108) % 2;
  if (gaze === 0 && browLift === 0 && mouthPull === 0) mouthPull = 1;
  return { gaze, browSide, browLift, mouthSide, mouthPull, wideEye };
}

// ---------------------------------------------------------------------------
// Raster kit
// ---------------------------------------------------------------------------

function put(buf: Uint8Array, x: number, y: number, v: number): void {
  if (x < 0 || x >= W || y < 0 || y >= W) return;
  buf[y * W + x] = v;
}

function get(buf: Uint8Array, x: number, y: number): number {
  if (x < 0 || x >= W || y < 0 || y >= W) return 0;
  return buf[y * W + x];
}

/** Writes x and its reflection about 31.5 (= `W - 1 - x`). Authored for x ≤ 31. */
function putSym(buf: Uint8Array, x: number, y: number, v: number): void {
  put(buf, x, y, v);
  put(buf, W - 1 - x, y, v);
}

function spanSym(buf: Uint8Array, x0: number, x1: number, y: number, v: number): void {
  for (let x = x0; x <= x1; x++) putSym(buf, x, y, v);
}

/** Paints only where the buffer already carries ink — keeps a feature inside the head. */
function shadeSymIfSolid(buf: Uint8Array, x: number, y: number, v: number): void {
  if (get(buf, x, y) !== 0) put(buf, x, y, v);
  const m = W - 1 - x;
  if (get(buf, m, y) !== 0) put(buf, m, y, v);
}

function shadeIfSolid(buf: Uint8Array, x: number, y: number, v: number): void {
  if (get(buf, x, y) !== 0) put(buf, x, y, v);
}

// ---------------------------------------------------------------------------
// Skull
// ---------------------------------------------------------------------------

/**
 * Half-widths per row, index 0 = row 0. Rebuilt per portrait into this one scratch;
 * `paintCaptain` is not re-entrant and does not need to be.
 */
const halfWidth = new Int32Array(W);

/** cranium × temple × ear × jaw × chin. `seed.ts` draws `faceShape` from this. */
export const FACE_SHAPES = 648;

/**
 * Half-widths at rows 8, 9, 10, 12, 15 — the crown, which the first version left
 * identical on every captain. Measured spread at row 8 is 10 px, which is the difference
 * between two people at a glance and the difference between two haircuts on one person.
 *
 * Nine archetypes, against five. The fifth to the ninth were bought by a measurement, not
 * by taste: once `hair` and `headwear` became optional layers, about one captain in
 * twenty has neither, and for that captain the skull IS the silhouette. With five crowns
 * the bare-headed cohort collapsed onto forty upper outlines and the head-twin rate over
 * 4.5 million pairs went from 0.32 % to 0.66 %. This is where that came back.
 */
const CRANIUM: readonly (readonly number[])[] = [
  [10, 12, 13, 14, 15], // 0 dome
  [13, 13, 14, 14, 15], // 1 flat-top
  [6, 8, 10, 12, 14], // 2 tapered
  [8, 11, 13, 14, 15], // 3 high and round
  [11, 12, 12, 13, 15], // 4 narrow flat
  [15, 15, 16, 16, 16], // 5 broad, squared crown
  [7, 9, 11, 13, 16], // 6 sharply tapered over a wide temple
  [12, 14, 15, 15, 14], // 7 heavy crown, narrow temple
  [9, 10, 11, 13, 13], // 8 small and high
];

/**
 * Temple width, carried down through the eye line. Narrower only: the cheek at row 30 is
 * the widest the head gets and the ear stands outside it, so growing here would push the
 * skull out of `SLOTS.skull.box`. Four widths at the eye line is what `WIDTH.cheek.tol`
 * buys, against one before.
 */
const TEMPLE_DELTA: readonly number[] = [-3, -2, -1, 0];
const JAW_DELTA: readonly number[] = [-3, 0, 3];
const CHIN_DELTA: readonly number[] = [-2, 0, 2];

/**
 * `[row, halfWidth]` for rows the cranium archetype does not own. The three anchored
 * widths (temple 16, cheek 17, jaw 13, chin 9) come straight from ANCHORS §4; the rest
 * interpolate between them.
 */
const SKULL_PROFILE: readonly (readonly [number, number])[] = [
  [20, 16],
  [26, 17],
  [30, 17],
  [35, 16],
  [40, 15],
  [44, 14],
  [48, 13],
  [52, 11],
  [ROW.jawBottom, 9],
];

function buildProfile(faceShape: number): void {
  const fs = ((faceShape % FACE_SHAPES) + FACE_SHAPES) % FACE_SHAPES;
  const crown = CRANIUM[fs % 9];
  const dTemple = TEMPLE_DELTA[Math.floor(fs / 9) % 4];
  const dJaw = JAW_DELTA[Math.floor(fs / 72) % 3];
  const dChin = CHIN_DELTA[Math.floor(fs / 216) % 3];
  for (let i = 0; i < W; i++) halfWidth[i] = 0;

  const points: [number, number][] = [
    [ROW.craniumTop, crown[0]],
    [9, crown[1]],
    [10, crown[2]],
    [12, crown[3]],
    [15, crown[4]],
    ...(SKULL_PROFILE as readonly [number, number][]),
  ];

  for (let seg = 0; seg < points.length - 1; seg++) {
    const [y0, w0] = points[seg];
    const [y1, w1] = points[seg + 1];
    for (let y = y0; y <= y1; y++) {
      const t = y1 === y0 ? 0 : (y - y0) / (y1 - y0);
      // Smoothstep rather than linear: a straight chord between control points shows
      // as a facet on a 34 px wide head.
      const e = t * t * (3 - 2 * t);
      let hw = w0 + (w1 - w0) * e;
      // Temple, full through the eye line and easing out above the brow and below the ear.
      const tw = Math.max(0, Math.min(1, Math.min((y - 15) / 4, (38 - y) / 4)));
      hw += dTemple * tw;
      // The jaw bump, centred on row 48 and dying out by rows 40 and 55.
      if (y >= 40) hw += dJaw * Math.max(0, 1 - Math.abs(y - 48) / 8);
      if (y >= 48) hw += dChin * Math.max(0, 1 - Math.abs(y - ROW.jawBottom) / 7);
      halfWidth[y] = Math.max(4, Math.round(hw));
    }
  }
}

function drawSkull(buf: Uint8Array, r: FaceRamp, a: Appearance): void {
  for (let y = ROW.craniumTop; y <= ROW.jawBottom; y++) {
    const hw = halfWidth[y];
    const x0 = 32 - hw;
    spanSym(buf, x0, 31, y, r.skinBase);
    putSym(buf, x0, y, r.skinEdge);
    // Terminator: two columns of shadow, inset one column from the silhouette edge, from
    // the temple down. The inset is the fix for the fourth defect and it is one pixel
    // wide: with the shadow flush against the edge, the outermost three columns of a
    // dark captain were contour, shadow, shadow — three values inside 0.06 luminance of
    // the panel, and the head had no edge at all. Leaving `skinBase` outermost puts the
    // lightest thing the tone owns where the silhouette is decided.
    if (y >= 14) {
      putSym(buf, x0 + 2, y, r.skinShadow);
      if (y >= 22) putSym(buf, x0 + 3, y, r.skinShadow);
    }
  }
  // Cranium cap and chin cap, so the outline closes.
  spanSym(buf, 32 - halfWidth[ROW.craniumTop], 31, ROW.craniumTop, r.skinEdge);
  spanSym(buf, 32 - halfWidth[ROW.jawBottom], 31, ROW.jawBottom, r.skinEdge);

  // Ears — a bulge contoured on the outside, rows 28…34, which is where an ear sits
  // relative to an eye line at 30. How far it stands off the skull is a face-shape trait
  // and it is worth spending: these rows fall inside the head mask the silhouette gate
  // measures, so an ear is one of the few outline cues that survives a hat.
  const earOut = 1 + (Math.floor(a.faceShape / 36) % 2);
  for (let y = 28; y <= 34; y++) {
    const x0 = 32 - halfWidth[y];
    const outer = x0 - earOut + 1;
    for (let x = outer; x <= x0; x++) putSym(buf, x, y, x === outer ? r.skinBase : r.skinShadow);
    putSym(buf, outer - 1, y, r.skinEdge);
  }

  // Neck, in the chin's shadow, and the trapezius line the collar sits on.
  for (let y = ROW.jawBottom + 1; y <= 60; y++) {
    spanSym(buf, 25, 31, y, r.skinShadow);
    putSym(buf, 25, y, r.skinEdge);
  }
  spanSym(buf, 26, 31, ROW.jawBottom + 1, r.skinShadow);

  // Forehead light. The face is lit from above-front, which is where the arena's own
  // light comes from, and the mass tapers with the dome instead of sitting on it as a
  // rectangle. Kept small on purpose: the lightest tones highlight with `pal.ui.text`,
  // which is over the bright-pass floor, and a ten-by-twelve patch of it comes back
  // through the bloom as a lamp. Four by seven comes back as a specular.
  const browTop = 12 + (a.faceShape % 2);
  for (let y = browTop; y <= browTop + 6; y++) {
    const spread = Math.min(4, Math.max(1, halfWidth[y] - 11));
    for (let x = 32 - spread; x <= 31; x++) shadeSymIfSolid(buf, x, y, r.skinLight);
  }

  // Cheekbone. Anchored to the silhouette rather than stamped at a pixel address: the
  // version that lived at columns 24–27 on every captain read as a painted V, and it was
  // the loudest thing on most faces after the eyes. Dropped entirely where `skinLight`
  // is over the bright-pass floor and would bloom the stroke into a stripe.
  if (!blooms(r.skinLight)) {
    const y0 = 33 + (a.faceShape % 3);
    const run = 2 + (a.ageBand >= 2 ? 1 : 0);
    for (let i = 0; i < run; i++) {
      const y = y0 + i;
      shadeSymIfSolid(buf, 32 - halfWidth[y] + 3 + i, y, r.skinLight);
    }
  }

  // Age. Not a wrinkle map — three marks that read at 3× and disappear at 1×.
  if (a.ageBand >= 2) {
    for (let y = 41; y <= 45; y++) shadeSymIfSolid(buf, 26 - Math.floor((y - 41) / 3), y, r.skinShadow);
    shadeSymIfSolid(buf, 27, 34, r.skinShadow);
    shadeSymIfSolid(buf, 28, 34, r.skinShadow);
  }
  if (a.ageBand >= 3) {
    spanSym(buf, 26, 31, 16, r.skinShadow);
    spanSym(buf, 27, 31, 19, r.skinShadow);
    for (let y = 33; y <= 35; y++) shadeSymIfSolid(buf, 21, y, r.skinShadow);
  }
}

// ---------------------------------------------------------------------------
// Brows
// ---------------------------------------------------------------------------

/**
 * Nine brows: three weights × three tilts, plus one raised side.
 *
 * Drawn per side rather than through `putSym`, because a raised brow is the cheapest
 * expression a 34 px face can carry and it is worth the six extra lines. Row centroid
 * still lands within `RC.anchorRowTol` of 26.
 */
function drawBrows(buf: Uint8Array, r: FaceRamp, brows: number, asym: Asymmetry): void {
  const weight = 1 + (brows % 3);
  const tilt = [-1, 0, 1][Math.floor(brows / 3) % 3];
  const x0 = 19;
  const x1 = 29;
  for (let side = 0; side < 2; side++) {
    const lift = side === asym.browSide ? asym.browLift : 0;
    for (let x = x0; x <= x1; x++) {
      // k runs 0 at the outer end to 1 at the inner end.
      const k = (x - x0) / (x1 - x0);
      const row = ROW.browLine + Math.round(tilt * (1 - 2 * k)) - lift;
      const top = row - Math.floor((weight - 1) / 2);
      for (let i = 0; i < weight; i++) {
        const v = i === 0 && weight >= 2 ? r.hairLight : r.hairDark;
        put(buf, side === 0 ? x : W - 1 - x, top + i, v);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Eyes
// ---------------------------------------------------------------------------

interface EyeSpec {
  upper: number;
  lower: number;
  halfW: number;
  iris: number;
  hood: number;
  /**
   * Lid curve. Below 1 the lid is round and the eye reads open; above 1 it comes to a
   * point at the corners and reads almond. The first eleven eyes were one cosine with
   * six numbers moved, which is why they were not nameable apart.
   */
  sharp: number;
  /** Outer-corner rise in px. +1 upturned, −1 downturned. */
  slant: number;
  /** 1 = the lower lid runs straight and stops short — a monolid or a deep-set eye. */
  flatLow: number;
}

/**
 * Eleven eyes. Shape first, then size: two eyes with the same `upper`/`lower` differ by
 * curve and slant, which is what the eye actually reads.
 */
const EYES: readonly EyeSpec[] = [
  { upper: 2, lower: 2, halfW: 5, iris: 2, hood: 0, sharp: 0.6, slant: 0, flatLow: 0 }, // round, alert
  { upper: 3, lower: 2, halfW: 5, iris: 2, hood: 1, sharp: 1.2, slant: -1, flatLow: 0 }, // hooded, tired
  { upper: 2, lower: 1, halfW: 6, iris: 2, hood: 0, sharp: 1.4, slant: 0, flatLow: 1 }, // narrow squint
  { upper: 3, lower: 2, halfW: 4, iris: 2, hood: 1, sharp: 0.9, slant: 0, flatLow: 0 }, // calm, half-lidded
  { upper: 3, lower: 2, halfW: 5, iris: 2, hood: 0, sharp: 0.5, slant: 0, flatLow: 0 }, // startled, wide
  { upper: 2, lower: 2, halfW: 4, iris: 1, hood: 2, sharp: 1.2, slant: -1, flatLow: 1 }, // deep-set, shadowed
  { upper: 3, lower: 3, halfW: 5, iris: 3, hood: 0, sharp: 0.7, slant: 1, flatLow: 0 }, // large iris, upturned
  { upper: 1, lower: 2, halfW: 4, iris: 1, hood: 0, sharp: 0.6, slant: 0, flatLow: 0 }, // small, beady
  { upper: 3, lower: 2, halfW: 4, iris: 2, hood: 1, sharp: 1.0, slant: 1, flatLow: 1 }, // high lid, almond
  { upper: 2, lower: 2, halfW: 5, iris: 2, hood: 0, sharp: 0.9, slant: -1, flatLow: 0 }, // heavy lower lid
  { upper: 4, lower: 2, halfW: 6, iris: 2, hood: 2, sharp: 1.1, slant: 0, flatLow: 0 }, // long, deeply hooded
];

function drawEyes(buf: Uint8Array, r: FaceRamp, eyes: number, asym: Asymmetry): void {
  const s = EYES[eyes % EYES.length];
  const cx = PUPIL_X[0] - 0.5;
  for (let eye = 0; eye < 2; eye++) {
    // A gaze is both irises off-centre in the SAME absolute direction. Mirroring them
    // would only make the eyes cross-eyed, which is why this loop is not `putSym`.
    // Only where the eye is wide enough to hold one: shifted inside a 7 px squint the
    // iris runs into the corner and the eye reads as an arrowhead rather than a look.
    const gaze = s.halfW >= 5 ? asym.gaze : 0;
    const irisAt = eye === 0 ? gaze : -gaze;
    // The iris may never fill the eye. Two columns of sclera at each corner is what makes
    // the difference between an eye and a dark slot.
    const irisW = Math.min(s.iris, Math.max(1, s.halfW - 3));
    const open = eye === asym.wideEye ? 1 : 0;
    for (let dx = -s.halfW; dx <= s.halfW; dx++) {
      const t = Math.abs(dx) / s.halfW;
      const bell = Math.pow(Math.cos((t * Math.PI) / 2), s.sharp);
      const up = Math.round((s.upper + open) * bell);
      const lo = s.flatLow === 1 ? (t < 0.8 ? s.lower : 0) : Math.round(s.lower * bell);
      // The outer corner rides up or down; the inner corner stays on the anchor.
      const rise = dx < 0 ? Math.round(s.slant * t) : 0;
      const authored = Math.round(cx + dx);
      const x = eye === 0 ? authored : W - 1 - authored;
      for (let dy = -up; dy <= lo; dy++) {
        const y = ROW.eyeLine + dy - rise;
        const hooded = dy < -up + s.hood;
        const inIris = Math.abs(dx - irisAt) <= irisW && dy >= -up + s.hood && dy <= lo - 1;
        let c: number;
        if (dy === lo || dy === -up || Math.abs(dx) === s.halfW) c = V.ink;
        else if (hooded) c = V.shade;
        // The pupil: the iris's own centre column, one step down, so an eleven-pixel eye
        // has somewhere to look from instead of being a slab of white with a blot in it.
        else if (inIris) c = dx === irisAt && r.iris > V.shade && r.iris < V.light ? V.shade : r.iris;
        else c = V.high;
        put(buf, x, y, c);
      }
    }
  }
}

/** Reference plate for the occlusion check. Not re-entrant; neither is `paintCaptain`. */
const eyeScratch = new Uint8Array(W * W);

/**
 * How much of each eye survived whatever was painted over it, as a fraction of the eye
 * the generator meant to draw.
 *
 * Measured against a clean re-draw rather than against a pixel count, because the two
 * eyes are not the same size once a captain has a gaze and a lid aperture: comparing
 * left against right would read a deliberate asymmetry as damage and undo it.
 */
export function eyeSurvival(buf: Uint8Array, a: Appearance): [number, number] {
  const r = faceRamp(a);
  eyeScratch.fill(0);
  drawEyes(eyeScratch, r, a.eyes, decodeAsymmetry(a.asymmetry));
  const [bx, by, bw, bh] = SLOTS.eyes.box;
  const want: [number, number] = [0, 0];
  const got: [number, number] = [0, 0];
  for (let y = by; y < by + bh; y++) {
    for (let x = bx; x < bx + bw; x++) {
      const i = y * W + x;
      if (eyeScratch[i] !== V.high) continue;
      const side = x < 32 ? 0 : 1;
      want[side]++;
      if (buf[i] === V.high) got[side]++;
    }
  }
  return [want[0] === 0 ? 1 : got[0] / want[0], want[1] === 0 ? 1 : got[1] / want[1]];
}

// ---------------------------------------------------------------------------
// Nose
// ---------------------------------------------------------------------------

/** Nine noses: three base widths × three bridge lengths. */
function drawNose(buf: Uint8Array, r: FaceRamp, nose: number): void {
  const wing = 2 + (nose % 3);
  const bridge = 1 + (Math.floor(nose / 3) % 3);

  for (let i = 0; i < bridge; i++) {
    const y = ROW.noseBase - 2 - i;
    putSym(buf, 30, y, r.skinShadow);
    putSym(buf, 31, y, r.skinBase);
  }
  spanSym(buf, 32 - wing, 31, ROW.noseBase - 1, r.skinShadow);
  spanSym(buf, 31 - wing, 31, ROW.noseBase, r.skinShadow);
  putSym(buf, 31 - wing, ROW.noseBase, V.ink);
  // Nostril, and the septum highlight that stops the base reading as a bar.
  putSym(buf, 31 - wing, ROW.noseBase + 1, V.ink);
  putSym(buf, 32 - wing, ROW.noseBase + 1, V.ink);
  putSym(buf, 31, ROW.noseBase - 1, r.skinLight);
}

// ---------------------------------------------------------------------------
// Mouth
// ---------------------------------------------------------------------------

interface MouthSpec {
  halfW: number;
  /** -1 pulled down, 0 level, +1 pulled up. */
  curve: number;
  /** Extra row of lower lip. */
  full: number;
  /** 1 = the upper lip is a thin line and the lower carries the volume. */
  thinTop: number;
  /** 1 = parted, with a row of teeth at the seam. */
  parted: number;
}

/**
 * Twelve mouths. `halfW` and `curve` alone gave ten mouths that were one line bent three
 * ways; a parted mouth and a thin upper lip are the two that change the read.
 */
const MOUTHS: readonly MouthSpec[] = [
  { halfW: 4, curve: 0, full: 0, thinTop: 0, parted: 0 },
  { halfW: 5, curve: 0, full: 0, thinTop: 1, parted: 0 },
  { halfW: 6, curve: 0, full: 0, thinTop: 0, parted: 0 },
  { halfW: 4, curve: -1, full: 0, thinTop: 1, parted: 0 },
  { halfW: 5, curve: -1, full: 1, thinTop: 0, parted: 0 },
  { halfW: 6, curve: -1, full: 0, thinTop: 1, parted: 0 },
  { halfW: 4, curve: 1, full: 1, thinTop: 0, parted: 0 },
  { halfW: 5, curve: 1, full: 0, thinTop: 0, parted: 1 },
  { halfW: 6, curve: 1, full: 1, thinTop: 0, parted: 0 },
  { halfW: 5, curve: 0, full: 1, thinTop: 0, parted: 1 },
  { halfW: 4, curve: 0, full: 0, thinTop: 1, parted: 1 },
  { halfW: 6, curve: -1, full: 1, thinTop: 0, parted: 1 },
];

function drawMouth(buf: Uint8Array, r: FaceRamp, mouth: number, asym: Asymmetry): void {
  const s = MOUTHS[mouth % MOUTHS.length];
  for (let side = 0; side < 2; side++) {
    const pull = side === asym.mouthSide ? asym.mouthPull : 0;
    for (let dx = 0; dx <= s.halfW; dx++) {
      const authored = 31 - dx;
      const x = side === 0 ? authored : W - 1 - authored;
      const k = dx / s.halfW;
      // One corner pulled, and only at the corner: `k*k` keeps the seam flat through the
      // middle so the row centroid stays on 46.
      const seam = ROW.mouthLine - Math.round(s.curve * k * 1.6) - Math.round(pull * k * k);
      const corner = dx === s.halfW;
      put(buf, x, seam, V.ink);
      if (corner) continue;
      put(buf, x, seam - 1, s.thinTop === 1 ? r.skinBase : r.skinShadow);
      if (s.parted === 1 && dx < s.halfW - 1) put(buf, x, seam + 1, V.light);
      else if (s.full === 1) put(buf, x, seam + 1, r.skinBase);
      // The lower lip catches light, but never the bright-pass value: a twelve-pixel bar
      // of a blooming highlight under the mouth comes back through the tube as a smile.
      // The fallback is the base, not a fixed ramp value — on a complexion ramp, dropping
      // to `pal.ui.text` would have painted a cyan lip on a brown face.
      put(buf, x, seam + 1 + s.full + s.parted, blooms(r.skinLight) ? r.skinBase : r.skinLight);
    }
  }
}

// ---------------------------------------------------------------------------
// Hair
// ---------------------------------------------------------------------------

interface HairSpec {
  /** Rows the mass rises above `craniumTop`. */
  rise: number;
  /** Extra half-width beyond the skull. */
  volume: number;
  /** 0 receded · 1 straight fringe · 2 widow's peak · 3 spiked · 4 swept · 5 crop. */
  fringe: number;
  /** Row the side mass reaches down to. */
  side: number;
  /** 0 round dome · 1 squared · 2 spiked · 3 a bound tail above the crown. */
  crown: number;
}

/**
 * Twenty-four styles, all of them real.
 *
 * Index 0 used to be "shaved", drawn as nothing. That spent one of twenty-four style
 * slots on absence and pinned the bald rate at 1/24 = 4.2 % for everybody, regardless of
 * age or of anything else. Absence is `presence` now (see `slots.ts`), index 0 is a buzz
 * cut, and how often a captain has no hair is drawn against age and reach.
 *
 * The first eighteen all sat inside the skull: `SLOTS.hair.box` allowed 8 px of growth
 * and `drawHeadwear` clamped it to 2, so hair — the only slot that can change a
 * silhouette — declined to. These spend it. The four at the end break the outline
 * hardest: a 7 px afro, a bound tail through the hair ceiling, a long mass to row 44, and
 * a stubbled scalp with a queue.
 */
const HAIRS: readonly HairSpec[] = [
  { rise: 1, volume: 0, fringe: 5, side: 16, crown: 0 }, // 0 buzz
  { rise: 1, volume: 1, fringe: 5, side: 20, crown: 0 }, // close crop
  { rise: 1, volume: 1, fringe: 0, side: 16, crown: 1 }, // receded crop
  { rise: 2, volume: 1, fringe: 1, side: 22, crown: 0 }, // straight fringe
  { rise: 2, volume: 1, fringe: 2, side: 22, crown: 1 }, // widow's peak
  { rise: 3, volume: 2, fringe: 1, side: 32, crown: 0 }, // heavy bob
  { rise: 3, volume: 1, fringe: 3, side: 18, crown: 2 }, // spiked
  { rise: 5, volume: 2, fringe: 4, side: 20, crown: 1 }, // swept back, tall
  { rise: 1, volume: 1, fringe: 0, side: 26, crown: 0 }, // receded, long sides
  { rise: 2, volume: 2, fringe: 2, side: 16, crown: 0 }, // tight peak
  { rise: 4, volume: 3, fringe: 2, side: 34, crown: 0 }, // full, peaked
  { rise: 2, volume: 3, fringe: 1, side: 38, crown: 1 }, // curtain
  { rise: 6, volume: 1, fringe: 3, side: 24, crown: 2 }, // tall spikes
  { rise: 1, volume: 2, fringe: 4, side: 30, crown: 0 }, // swept, low
  { rise: 3, volume: 1, fringe: 5, side: 12, crown: 1 }, // pad
  { rise: 2, volume: 4, fringe: 1, side: 28, crown: 0 }, // wide fringe
  { rise: 5, volume: 4, fringe: 2, side: 30, crown: 1 }, // tall and full
  { rise: 1, volume: 1, fringe: 1, side: 14, crown: 0 }, // short fringe
  { rise: 6, volume: 6, fringe: 5, side: 30, crown: 0 }, // afro
  { rise: 8, volume: 7, fringe: 1, side: 34, crown: 0 }, // big afro
  { rise: 9, volume: 1, fringe: 5, side: 22, crown: 3 }, // bound topknot
  { rise: 3, volume: 5, fringe: 1, side: 44, crown: 0 }, // long, past the jaw
  { rise: 2, volume: 4, fringe: 2, side: 42, crown: 1 }, // long curtain
  { rise: 10, volume: 1, fringe: 0, side: 10, crown: 3 }, // shaved with a queue
];

/** Hairline row for a given column, per fringe type. Rows below this stay skin. */
function fringeRow(fringe: number, dx: number): number {
  switch (fringe) {
    case 0:
      return dx > 9 ? 20 : 13; // receded at the temples, higher in the middle
    case 1:
      // A ruled hairline reads as a headband, so the fringe carries a fixed 1 px wave.
      return 23 - (dx % 5 === 2 ? 1 : 0);
    case 2:
      return dx <= 2 ? 24 : 22 - Math.min(6, dx - 2); // widow's peak
    case 3:
      return 17;
    case 4:
      return 14;
    default:
      return 15;
  }
}

/**
 * Hair body and hat fit, from one gene.
 *
 * Twenty-four styles crossed with ten hats still put two captains in 384 under a
 * bit-identical head outline, because a hat swallows the style underneath it. A pixel of
 * body either way, and a row of how low the hat is worn, are both things you would notice
 * about a person and both things that change a silhouette.
 */
export function decodeBuild(n: number): { body: number; fit: number; drop: number } {
  const v = ((n % 27) + 27) % 27;
  return {
    body: [-1, 0, 1][v % 3],
    fit: Math.floor(v / 3) % 3,
    drop: [-3, 0, 4][Math.floor(v / 9) % 3],
  };
}

/** A head with no hair on it. What `drawHeadwear` fits itself to when `hair` is absent. */
const BALD: HairSpec = { rise: 0, volume: 0, fringe: 5, side: 0, crown: 0 };

/** The style as this captain wears it. */
function wornHair(s: HairSpec, body: number, drop: number): HairSpec {
  // Floored at 1, not 0. Hair that hugs the skull exactly is hair the silhouette cannot
  // see, and a thinner captain still has to look like a different captain.
  return {
    ...s,
    rise: Math.max(1, s.rise + body),
    volume: Math.max(1, s.volume + body),
    side: Math.max(ROW.craniumTop + 2, Math.min(46, s.side + body * 2 + drop)),
  };
}

/** The mass above `craniumTop`, which is where a hairstyle becomes a silhouette. */
function crownWidth(s: HairSpec, y: number, top: number, base: number): number {
  const rise = Math.max(1, ROW.craniumTop - top);
  const k = (ROW.craniumTop - y) / (rise + 0.6);
  switch (s.crown) {
    case 1: // squared — keeps its width, then closes in the last two rows
      return k >= 1 ? 0 : Math.round(base * (k > 0.82 ? 0.7 : 1));
    case 3: // a bound tail: a narrow column that runs far above the crown
      return k >= 1 ? 0 : Math.max(3, Math.round(base * 0.32));
    default:
      return Math.round(base * Math.sqrt(Math.max(0, 1 - k * k)));
  }
}

function drawHair(buf: Uint8Array, r: FaceRamp, s: HairSpec): void {
  const top = ROW.craniumTop - s.rise;
  const base = halfWidth[ROW.craniumTop] + s.volume;

  for (let y = Math.max(ROW.hairCeil - 2, top); y <= s.side; y++) {
    let hw =
      y >= ROW.craniumTop
        ? halfWidth[Math.min(y, ROW.jawBottom)] + s.volume
        : crownWidth(s, y, top, base);
    hw = Math.min(hw, 25);
    if (hw <= 0) continue;

    for (let x = 32 - hw; x <= 31; x++) {
      const dx = 31 - x;
      // Inside the hairline the hair only paints where it is above the fringe row.
      if (y >= ROW.craniumTop && y > fringeRow(s.fringe, dx)) {
        // Below the hairline, only the outer sides carry hair.
        if (dx < halfWidth[Math.min(y, ROW.jawBottom)] - 2) continue;
      }
      if (s.crown === 2 && y < ROW.craniumTop && dx % 3 === 1) continue; // spikes
      const edge = x === 32 - hw;
      const lit = y <= top + 2;
      putSym(buf, x, y, edge ? r.hairEdge : lit ? r.hairLight : r.hairDark);
    }
  }
  // The hairline itself, contoured so the mass separates from the forehead.
  for (let dx = 0; dx <= 22; dx++) {
    const fy = fringeRow(s.fringe, dx);
    const x = 31 - dx;
    if (get(buf, x, fy) === r.hairDark || get(buf, x, fy) === r.hairLight) putSym(buf, x, fy, r.hairEdge);
  }
}

// ---------------------------------------------------------------------------
// Facial hair
// ---------------------------------------------------------------------------

/**
 * Eight shapes, all of them real. Index 0 was "none" and is now stubble; whether a
 * captain has facial hair at all is the `facialHair` bit of `presence`.
 */
function drawFacialHair(buf: Uint8Array, r: FaceRamp, kind: number): void {
  const dark = r.hairDark;

  const jawFill = (yTop: number, yBottom: number, inset: number): void => {
    for (let y = yTop; y <= yBottom; y++) {
      const hw = halfWidth[Math.min(y, ROW.jawBottom)];
      for (let x = 32 - hw + inset; x <= 31; x++) shadeSymIfSolid(buf, x, y, dark);
    }
  };

  switch (kind) {
    case 0: // stubble — a dither, so it reads as texture and not as a beard
      for (let y = 40; y <= ROW.jawBottom; y++) {
        const hw = halfWidth[y];
        for (let x = 32 - hw + 1; x <= 31; x++) if ((x + y) % 2 === 0) shadeSymIfSolid(buf, x, y, r.skinShadow);
      }
      for (let y = 43; y <= 45; y++) for (let x = 25; x <= 31; x++) if ((x + y) % 2 === 0) shadeSymIfSolid(buf, x, y, r.skinShadow);
      break;
    case 1: // moustache
      for (let y = 43; y <= 45; y++) spanSym(buf, 26, 31, y, y === 43 ? r.hairLight : dark);
      break;
    case 2: // goatee
      for (let y = 48; y <= 54; y++) spanSym(buf, 28, 31, y, dark);
      break;
    case 3: // chinstrap — follows the silhouette edge only
      for (let y = 34; y <= ROW.jawBottom; y++) {
        const hw = halfWidth[y];
        shadeSymIfSolid(buf, 32 - hw + 1, y, dark);
        shadeSymIfSolid(buf, 32 - hw + 2, y, dark);
      }
      break;
    case 4: // short full beard
      jawFill(44, ROW.jawBottom, 1);
      for (let y = 43; y <= 45; y++) spanSym(buf, 26, 31, y, dark);
      break;
    case 5: // long full beard, spilling onto the collar
      jawFill(42, ROW.jawBottom, 1);
      for (let y = ROW.jawBottom + 1; y <= 61; y++) spanSym(buf, 32 - (10 - (y - ROW.jawBottom)), 31, y, dark);
      for (let y = 43; y <= 45; y++) spanSym(buf, 25, 31, y, dark);
      break;
    case 6: // mutton chops
      for (let y = 32; y <= 48; y++) {
        const hw = halfWidth[y];
        for (let x = 32 - hw + 1; x <= 32 - hw + 3; x++) shadeSymIfSolid(buf, x, y, dark);
      }
      break;
    default: // 7 van dyke
      for (let y = 43; y <= 45; y++) spanSym(buf, 26, 31, y, dark);
      for (let y = 48; y <= 53; y++) spanSym(buf, 29, 31, y, dark);
      break;
  }

  // The moustache/beard break. Without it a full beard fills as one value from the nose
  // to the collar and the lower face is a bib: 4.2 % of captains shipped with no mouth
  // ink at all. The mouth is re-stamped after this, and this row is what stops the
  // moustache and the beard reading as the same mass.
  if (kind === 4 || kind === 5 || kind === 7) {
    for (let x = 24; x <= 31; x++) shadeSymIfSolid(buf, x, ROW.mouthLine - 3, r.hairLight);
  }
}

// ---------------------------------------------------------------------------
// Headwear
// ---------------------------------------------------------------------------

export const HEADWEAR_COUNT = 9;

/**
 * Nine shapes, all of them real. Index 0 was "none"; whether a captain is bare-headed is
 * the `headwear` bit of `presence`, and that separation is what took the hat rate from
 * 88.2 % of the roster down to about 55 %.
 *
 * `hair` is passed in because a hat sits ON hair — or on a bare skull, when the hair
 * layer is absent, in which case `BALD` is what arrives here. Without it the headwear
 * shapes erase twenty-four hair silhouettes down to nine, and the distinctness scan finds
 * exactly that collapse: its worst pairs were two captains in the same cap.
 *
 * The clamps used to be `min(2, …)`, which meant a cap over an afro drew the same outline
 * as a cap over a crop. They are `min(7, …)` now, so the hat carries the hair's mass.
 */
function drawHeadwear(
  buf: Uint8Array,
  r: FaceRamp,
  kind: number,
  hair: HairSpec,
  warmBand: boolean,
  fit: number
): void {
  const lift = Math.min(7, hair.rise) + fit;
  const pad = Math.min(7, hair.volume) + fit;
  const band = warmBand ? V.warm : r.clothLight;
  // How low it is worn. Ten shapes over twenty-four hairstyles still collided on the
  // outline once a cap had swallowed the hair; a row of play breaks the tie.
  const dy = fit - 1;
  const cap = (yTop0: number, yBottom0: number, grow0: number, fill: number): void => {
    const yBottom = yBottom0 + dy;
    const yTop = Math.max(ROW.hairCeil - 2, yTop0 - lift);
    const grow = Math.max(0, grow0 + pad);
    for (let y = yTop; y <= yBottom; y++) {
      // Above the cranium the hat takes the hair's own crown shape. Without that, a cap
      // over an afro and a cap over a crop drew the same outline and the distinctness
      // scan's worst pairs were two captains in the same hat.
      let hw =
        y >= ROW.craniumTop
          ? halfWidth[Math.min(y, ROW.jawBottom)] + grow
          : crownWidth(hair, y, yTop, halfWidth[ROW.craniumTop] + grow);
      hw = Math.min(hw, 25);
      if (hw <= 0) continue;
      spanSym(buf, 32 - hw, 31, y, fill);
      putSym(buf, 32 - hw, y, r.clothEdge);
    }
  };

  switch (kind) {
    case 0: // watch cap
      cap(ROW.hairCeil + 1, 18, 1, r.clothMid);
      spanSym(buf, 32 - halfWidth[16] - 1, 31, 17, r.clothLight);
      spanSym(buf, 32 - halfWidth[18] - 1, 31, 18, r.clothEdge);
      break;
    case 1: // cadet cap with a brim
      cap(ROW.hairCeil, 15, 1, r.clothMid);
      spanSym(buf, 14, 31, 16, r.clothDark);
      spanSym(buf, 14, 31, 17, r.clothEdge);
      break;
    case 2: // hood — the drape follows the head under it, so two hoods are two people
      cap(ROW.hairCeil - 2, 22, 3, r.clothDark);
      for (let y = 22; y <= 40 + dy; y++) {
        const hw = halfWidth[y] + Math.min(7, 3 + pad);
        spanSym(buf, 32 - hw, 32 - hw + 2, y, r.clothDark);
        putSym(buf, 32 - hw, y, r.clothEdge);
        putSym(buf, 32 - hw + 3, y, V.ink);
      }
      break;
    case 3: // peaked officer cap — the headwear that can carry the warm accent
      cap(ROW.hairCeil, 13, 2, r.clothMid);
      spanSym(buf, 32 - halfWidth[14] - 2, 31, 14, r.clothEdge);
      spanSym(buf, 32 - halfWidth[15] - 2, 31, 15, band);
      spanSym(buf, 12, 31, 16, r.clothDark);
      spanSym(buf, 12, 31, 17, r.clothEdge);
      break;
    case 4: // comms headset — a band and an ear puck
      cap(ROW.hairCeil + 2, 10, 1, r.clothMid);
      for (let y = 10; y <= 27; y++) putSym(buf, 32 - halfWidth[Math.max(y, ROW.craniumTop)] - 1 - pad, y, r.clothMid);
      for (let y = 27; y <= 34; y++) {
        const hw = halfWidth[y] + 1 + Math.min(3, pad);
        spanSym(buf, 32 - hw, 32 - hw + 2, y, y === 27 || y === 34 ? r.clothEdge : r.clothLight);
      }
      break;
    case 5: // skullcap and band
      cap(ROW.hairCeil + 2, 14, 0, r.clothDark);
      spanSym(buf, 32 - halfWidth[15] - pad, 31, 15, r.clothLight);
      putSym(buf, 32 - halfWidth[15] - pad, 15, r.clothEdge);
      spanSym(buf, 32 - halfWidth[16], 31, 16, r.clothEdge);
      break;
    case 6: // bandana, knotted at one side
      cap(ROW.craniumTop, 14, 1, r.clothMid);
      spanSym(buf, 32 - halfWidth[15] - 1, 31, 15, r.clothEdge);
      for (let y = 12; y <= 18; y++) putSym(buf, 32 - halfWidth[14] - 3, y, r.clothMid);
      putSym(buf, 32 - halfWidth[14] - 4, 15, r.clothEdge);
      break;
    case 7: // cowl
      cap(ROW.hairCeil - 1, 20, 2, r.clothDark);
      for (let y = 20; y <= 34 + dy; y++) {
        const hw = halfWidth[y] + Math.min(6, 2 + pad);
        spanSym(buf, 32 - hw, 32 - hw + 1, y, r.clothDark);
        putSym(buf, 32 - hw, y, r.clothEdge);
      }
      break;
    default: // 8 goggles pushed up onto the forehead
      spanSym(buf, 32 - halfWidth[19] - 1, 31, 19, r.clothDark);
      for (let y = 20; y <= 23; y++) spanSym(buf, 19, 26, y, y === 20 || y === 23 ? V.ink : r.clothLight);
      for (let y = 20; y <= 23; y++) spanSym(buf, 27, 31, y, r.clothDark);
      putSym(buf, 22, 21, V.high);
      break;
  }
}

// ---------------------------------------------------------------------------
// Collar and rank
// ---------------------------------------------------------------------------

function drawCollar(buf: Uint8Array, r: FaceRamp, uniform: number, warmCord: boolean): void {
  // Shoulders. Everything else in this slot sits on top of them.
  for (let y = ROW.shoulderLine; y < W; y++) {
    const hw = Math.min(28, 18 + (y - ROW.shoulderLine) * 3);
    spanSym(buf, 32 - hw, 31, y, r.clothMid);
    putSym(buf, 32 - hw, y, r.clothEdge);
  }
  spanSym(buf, 32 - 18, 31, ROW.shoulderLine, r.clothEdge);

  switch (uniform % 8) {
    case 0: // open neck
      for (let y = ROW.shoulderLine; y <= 62; y++) spanSym(buf, 32 - (y - ROW.shoulderLine) - 2, 31, y, r.skinShadow);
      for (let y = ROW.shoulderLine; y <= 62; y++) putSym(buf, 32 - (y - ROW.shoulderLine) - 3, y, r.clothEdge);
      break;
    case 1: // high buttoned
      for (let y = 54; y <= ROW.shoulderLine + 2; y++) {
        spanSym(buf, 22, 31, y, r.clothMid);
        putSym(buf, 22, y, r.clothEdge);
      }
      putSym(buf, 31, 57, r.clothLight);
      break;
    case 2: // storm collar, turned up
      for (let y = 53; y <= 61; y++) {
        const hw = 12 + (y - 53);
        spanSym(buf, 32 - hw, 32 - hw + 3, y, r.clothLight);
        putSym(buf, 32 - hw, y, r.clothEdge);
        putSym(buf, 32 - hw + 4, y, V.ink);
      }
      break;
    case 3: // rig harness
      for (let y = ROW.shoulderLine; y < W; y++) {
        spanSym(buf, 24, 27, y, r.clothDark);
        putSym(buf, 24, y, r.clothEdge);
        putSym(buf, 27, y, V.ink);
      }
      spanSym(buf, 20, 31, 61, r.clothDark);
      spanSym(buf, 20, 31, 60, r.clothEdge);
      break;
    case 4: // vestments — a wide yoke with a cord
      for (let y = 57; y <= 62; y++) spanSym(buf, 32 - (10 + (y - 57) * 2), 31, y, r.clothLight);
      for (let y = 57; y <= 62; y++) putSym(buf, 32 - (10 + (y - 57) * 2), y, r.clothEdge);
      spanSym(buf, 29, 31, 63, warmCord ? V.warm : r.clothLight);
      break;
    case 5: // armoured yoke
      for (let y = ROW.shoulderLine + 1; y <= 61; y++) {
        spanSym(buf, 14, 22, y, r.clothLight);
        putSym(buf, 14, y, r.clothEdge);
        putSym(buf, 22, y, V.ink);
      }
      spanSym(buf, 14, 22, ROW.shoulderLine + 1, r.clothEdge);
      break;
    case 6: // scarf
      for (let y = 54; y <= 60; y++) spanSym(buf, 32 - (13 + (y - 54)), 31, y, r.clothLight);
      for (let y = 54; y <= 60; y++) putSym(buf, 32 - (13 + (y - 54)), y, r.clothEdge);
      for (let y = 58; y <= 63; y++) spanSym(buf, 26, 29, y, r.clothMid);
      break;
    default: // fatigues
      for (let y = 56; y <= 62; y++) {
        spanSym(buf, 32 - (8 + (y - 56)), 31, y, r.skinShadow);
        putSym(buf, 32 - (8 + (y - 56)), y, r.clothEdge);
      }
      break;
  }
}

/**
 * The rank flash: one warm bar on the shoulder, under the jaw.
 *
 * It used to be two or three 1×1 gold dots at x 42–53 on near-black, out in the corner
 * of the card. At `HUD_SCALE` they were noise and at `CARD_SCALE` they were the brightest
 * thing in the frame on a dark captain, pulling the eye off the face. One bar, seated
 * against the neck, frames the jaw instead of competing with it. Length carries the rank.
 */
function drawRank(buf: Uint8Array, r: FaceRamp, uniform: number, ageBand: number, warm: boolean): void {
  const pips = 1 + ((uniform + ageBand) % 3);
  const [bx, by] = SLOTS.rank.box;
  // On the shoulder, outboard of the neck (which owns columns 25…38). Seated under the
  // jaw on one side, it frames the face; seated under the chin it reads as jewellery.
  const y = by + 2;
  const x0 = bx + 1;
  const x1 = x0 + 2 + pips;
  for (let x = x0; x <= x1; x++) {
    if (get(buf, x, y) === 0) continue;
    put(buf, x, y, warm ? V.warm : r.clothLight);
    if (get(buf, x, y + 1) !== 0) put(buf, x, y + 1, V.ink);
  }
}

// ---------------------------------------------------------------------------
// Marks — always present, never mirrored
// ---------------------------------------------------------------------------

/**
 * Twelve marks, each on a sub-seed.
 *
 * Every one of them used to be a rectangle at a literal address: two captains with the
 * same mark index wore a pixel-identical decal, and two of the twelve covered an entire
 * eye. `markVar` moves the mark, flips which side of the face it is on, and scales how
 * far it runs, so a scar is a scar this captain has rather than a sticker the generator
 * owns. `paintCaptain` then guarantees the eye survives it.
 */
interface MarkPlace {
  dx: number;
  dy: number;
  /** 0 = the captain's left (low columns), 1 = mirrored to the right. */
  side: number;
  /** Length multiplier, 0.6…1.4. */
  run: number;
}

function markPlace(markVar: number): MarkPlace {
  const v = ((markVar % 30) + 30) % 30;
  return {
    dx: [-3, -1, 0, 1, 3][v % 5],
    dy: [-2, 0, 1][Math.floor(v / 5) % 3],
    side: Math.floor(v / 15) % 2,
    run: [0.6, 1.0, 1.4][Math.floor(v / 10) % 3],
  };
}

function drawMark(buf: Uint8Array, r: FaceRamp, mark: number, place: MarkPlace): void {
  // Authored on the left of the face; `place` moves it and decides which cheek it is on.
  // Everything goes through `inBox`, because an offset mark that wanders out of
  // `SLOTS.marks` lands where the mirror gate is still watching and breaks it.
  const [bx, by, bw, bh] = SLOTS.marks.box;
  const inBox = (x: number, y: number): boolean => x >= bx && x < bx + bw && y >= by && y < by + bh;
  const px = (x: number): number => (place.side === 0 ? x + place.dx : W - 1 - (x + place.dx));
  const m = (x: number, y: number, v: number): void => {
    const xx = px(x);
    const yy = y + place.dy;
    if (inBox(xx, yy)) shadeIfSolid(buf, xx, yy, v);
  };
  const mAt = (x: number, y: number, v: number): void => {
    if (inBox(x, y)) shadeIfSolid(buf, x, y, v);
  };
  const len = (n: number): number => Math.max(2, Math.round(n * place.run));

  switch (mark % 12) {
    case 0: {
      // scar across the brow and eye
      const n = len(12);
      for (let i = 0; i <= n; i++) {
        const x = 24 - Math.floor(i / 3);
        m(x, 22 + i, V.ink);
        m(x + 1, 22 + i, r.skinLight);
      }
      break;
    }
    case 1: // burn, stippled over the cheek
      for (let y = 32; y <= 45; y++) {
        for (let x = 17; x <= 27; x++) {
          const d = ((x - 22) * (x - 22) + (y - 38) * (y - 38) * 0.6) / place.run;
          if (d < 30 && (x * 3 + y * 5) % 4 !== 0) m(x, y, d < 12 ? r.skinShadow : r.skinShadow);
        }
      }
      break;
    case 2: // tattoo, chevrons on the temple
      for (let i = 0; i < Math.max(2, Math.round(3 * place.run)); i++) {
        const y = 23 + i * 3;
        for (let k = 0; k < 3; k++) {
          m(17 + k, y + k, V.high);
          m(17 + k, y - k, V.high);
        }
      }
      break;
    case 3: {
      // ocular implant — a frame around the eye, not a lid over it. The 9×7 filled box
      // this used to be blanked one eye on 8.8 % of the roster.
      const x0 = 18;
      const x1 = 29;
      for (let x = x0; x <= x1; x++) {
        m(x, 26, V.ink);
        m(x, 34, V.ink);
      }
      for (let y = 26; y <= 34; y++) {
        m(x0, y, V.ink);
        m(x0 + 1, y, V.mid);
        m(x1, y, V.mid);
      }
      m(x0 + 1, 26, V.spec);
      m(x0 + 1, 34, V.mid);
      break;
    }
    case 4: // missing ear
      for (let y = 27; y <= 35; y++) {
        const hw = halfWidth[Math.min(y, ROW.jawBottom)];
        const xx = place.side === 0 ? 32 - hw : 31 + hw;
        const out = place.side === 0 ? xx - 1 : xx + 1;
        const yy = y + place.dy;
        if (!inBox(xx, yy)) continue;
        if (inBox(out, yy)) put(buf, out, yy, 0);
        put(buf, xx, yy, y % 2 === 0 ? r.skinEdge : r.skinShadow);
      }
      break;
    case 5: // cauterised socket — the lid is gone, the eye is not
      for (let i = 0; i <= 11; i++) m(18 + i, 25 + Math.round(i * 0.4), V.ink);
      for (let i = 0; i <= 11; i++) m(18 + i, 35 - Math.round(i * 0.3), V.ink);
      for (let y = 25; y <= 27; y++) for (let x = 18; x <= 29; x++) m(x, y, r.skinShadow);
      for (let y = 34; y <= 35; y++) for (let x = 18; x <= 29; x++) m(x, y, r.skinShadow);
      break;
    case 6: // brand on the cheek
      for (let y = 36; y <= 41; y++) {
        for (let x = 19; x <= 24; x++) {
          const ring = y === 36 || y === 41 || x === 19 || x === 24;
          if (ring) m(x, y, V.ink);
        }
      }
      for (let x = 21; x <= 22; x++) for (let y = 38; y <= 39; y++) m(x, y, r.skinLight);
      break;
    case 7: // freckling
      for (let y = 33; y <= 43; y++) {
        for (let x = 18; x <= 45; x++) {
          if ((x * 7 + y * 13) % 11 === (place.dx + 3) % 11) mAt(x, y + place.dy, r.skinShadow);
        }
      }
      break;
    case 8: // vitiligo — a depigmented patch, edge stippled so it reads as skin
      for (let y = 33; y <= 45; y++) {
        for (let x = 17; x <= 27; x++) {
          const d = ((x - 22) * (x - 22) * 0.8 + (y - 39) * (y - 39) * 0.7) / place.run;
          if (d < 8) m(x, y, r.skinLight);
          else if (d < 14 && (x * 3 + y * 7) % 3 !== 0) m(x, y, r.skinLight);
        }
      }
      break;
    case 9: {
      // chipped tooth, visible at the seam — central, so it ignores `side`
      const wide = place.run > 1 ? 1 : 0;
      const row = ROW.mouthLine + (place.dy > 0 ? 1 : 0);
      for (let x = 29 + place.dx - wide; x <= 31 + place.dx; x++) {
        mAt(x, row, V.high);
        mAt(x, row + 1, V.spec);
      }
      mAt(28 + place.dx - wide, row, V.ink);
      break;
    }
    case 10: // split brow
      for (let y = 23; y <= 29; y++) {
        m(22, y, r.skinBase);
        m(23, y, r.skinLight);
      }
      m(22, 23, V.ink);
      m(22, 29, V.ink);
      break;
    default: {
      // jaw plate
      const y1 = 41 + len(11);
      for (let y = 41; y <= y1; y++) {
        for (let x = 17; x <= 28; x++) {
          const edge = y === 41 || y === y1 || x === 17 || x === 28;
          m(x, y, edge ? V.ink : V.mid);
        }
      }
      m(20, 44, V.spec);
      m(25, y1 - 3, V.spec);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Composite
// ---------------------------------------------------------------------------

/**
 * Paints one captain into `buf` (length 64×64) as ramp indices, back to front.
 *
 * The order is `LAYERS` in `slots.ts` and nowhere else. It used to be the call sequence
 * of this function, restated in a sentence in ANCHORS §5 — two places that could
 * disagree, and no way to notice when they did. This loop reads the table, a test reads
 * the table, and the prose cites it.
 *
 * Two of the orderings are load-bearing and both were bought with a defect: the mouth is
 * stamped AFTER the facial hair, because a full beard filled as one value and 85 of 2 048
 * captains shipped with no mouth at all; and the mark is checked against the eyes it
 * might have covered, because two of the twelve marks were exactly the size of an eye
 * socket.
 *
 * Optional layers are skipped, not drawn as empty. A captain with no hair is not a
 * captain wearing hairstyle zero — the hat that goes on afterwards fits the bare skull.
 *
 * Pure and allocation-free apart from the shared profile scratch. Same appearance in,
 * identical pixels out.
 */
export function paintCaptain(a: Appearance, buf: Uint8Array): void {
  buf.fill(0);
  const r = faceRamp(a);
  const asym = decodeAsymmetry(a.asymmetry);
  const build = decodeBuild(a.build);
  const worn = hasLayer(a.presence, 'hair')
    ? wornHair(HAIRS[a.hair % HAIRS.length], build.body, build.drop)
    : BALD;
  buildProfile(a.faceShape);

  for (const layer of LAYERS) {
    if (!hasLayer(a.presence, layer.id)) continue;
    switch (layer.id) {
      case 'collar':
        drawCollar(buf, r, a.uniform, r.warm === 'collar');
        break;
      case 'skull':
        drawSkull(buf, r, a);
        break;
      case 'hair':
        drawHair(buf, r, worn);
        break;
      case 'brow':
        drawBrows(buf, r, a.brows, asym);
        break;
      case 'eyes':
        drawEyes(buf, r, a.eyes, asym);
        break;
      case 'nose':
        drawNose(buf, r, a.nose);
        break;
      case 'facialHair':
        drawFacialHair(buf, r, a.facialHair);
        break;
      case 'mouth':
        drawMouth(buf, r, a.mouth, asym);
        break;
      case 'marks': {
        drawMark(buf, r, a.mark, markPlace(a.markVar));
        // A mark may take half an eye. It may not take an eye: 181 of 2 048 captains used
        // to render with one eye carrying zero sclera, and the mirror gate exempts the
        // `marks` slot, so nothing could catch it. A captain who cannot look at you is a
        // design choice, and it is not one a decal gets to make by accident.
        const kept = eyeSurvival(buf, a);
        if (kept[0] < RC.minEyeSclera || kept[1] < RC.minEyeSclera) drawEyes(buf, r, a.eyes, asym);
        break;
      }
      case 'headwear':
        drawHeadwear(buf, r, a.headwear, worn, r.warm === 'cap', build.fit);
        break;
      case 'rank':
        drawRank(buf, r, a.uniform, a.ageBand, r.warm === 'rank');
        break;
    }
  }
}

/** Read-only view of the skull profile, for the distinctness metric. */
export function skullHalfWidths(faceShape: number): Int32Array {
  buildProfile(faceShape);
  return halfWidth;
}

/** Part-set sizes this file owns, so `seed.ts` cannot draw an index that does not exist. */
export const HAIR_STYLES = HAIRS.length;
export const MOUTH_SHAPES = MOUTHS.length;
